import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import {
  MessageCircle,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Voicemail as VoicemailIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { cn } from '@/lib/utils'
import type { TimelineEvent } from '@/types/timeline'
import { ActionsResumeAppel } from './ActionsResumeAppel'
import { useOuvrirDetailsAppel } from './detailsAppelContext'
import { DiscussionEquipe } from './DiscussionEquipe'
import { DossierClio } from './DossierClio'
import { LecteurAppel } from './LecteurAppel'
import { SMSMediaList } from './SMSMedia'

interface Props {
  event: TimelineEvent
  darkMode?: boolean
  /** Le numéro du contact : sert à lire l'enregistrement de l'appel. */
  phoneNumber?: string
  /** La ligne du fil : une discussion d'équipe s'y rattache. */
  line?: string | null
  /** Plan Team : commenter un appel ou un texto pour l'équipe. */
  equipe?: boolean
}

function formatTime(iso: string, locale: 'fr' | 'en') {
  try {
    return format(new Date(iso), 'HH:mm', { locale: locale === 'fr' ? fr : enUS })
  } catch {
    return ''
  }
}

function formatDuration(seconds?: number) {
  if (!seconds) return null
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${secs}s` : `${mins} min`
}

/** Le texte d'une note, les « @Nom » des personnes citées en évidence. */
function TexteAvecMentions({ texte, noms, darkMode }: { texte: string; noms: string[]; darkMode: boolean }) {
  const cibles = noms.filter(Boolean).map((n) => `@${n}`)
  if (!cibles.length) return <>{texte}</>
  const echapper = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const morceaux = texte.split(new RegExp(`(${cibles.map(echapper).join('|')})`, 'g'))
  return (
    <>
      {morceaux.map((m, i) =>
        cibles.includes(m) ? (
          <span
            key={i}
            className={cn('font-medium', darkMode ? 'text-indigo-300' : 'text-indigo-600')}
          >
            {m}
          </span>
        ) : (
          m
        ),
      )}
    </>
  )
}

/**
 * Carte d'événement de la timeline contact.
 *
 * Layout calqué sur le Next : alignement par direction (comme une conversation).
 * - SMS inbound, call_inbound, voicemail → à gauche
 * - SMS outbound, call_outbound → à droite
 * - Note, AI summary → pleine largeur centrée
 */
export function TimelineEventCard({ event, darkMode = false, phoneNumber, line = null, equipe = false }: Props) {
  const { t } = useTranslation()
  const ouvrirDetails = useOuvrirDetailsAppel()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const time = formatTime(event.timestamp, locale as 'fr' | 'en')

  const isOutbound =
    event.type === 'call_outbound' || event.type === 'sms_outbound'
  const isCentered = event.type === 'note'

  // Note interne de l'équipe (plan Team) : ambrée, jamais envoyée au client.
  // Note interne : sobre, comme une remarque d'un collègue en marge du fil.
  // La bordure pointillée dit « pas envoyé au client » sans alarmer.
  if (event.type === 'internal_note') {
    const mentions = event.details.mentions ?? []
    return (
      <div className="flex gap-2 w-full max-w-[520px] mx-auto">
        <div
          className={cn(
            'mt-0.5 h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center text-[9.5px] font-semibold',
            darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-200 text-slate-600',
          )}
        >
          {event.agent_initials || '?'}
        </div>
        <div className="min-w-0 flex-1">
          <div className={cn('text-[11px]', darkMode ? 'text-slate-500' : 'text-slate-400')}>
            <span className={cn('font-medium', darkMode ? 'text-slate-300' : 'text-slate-600')}>
              {event.agent_name}
            </span>
            {' · '}
            {t('inbox.team.noteShort', 'note interne')}
            {' · '}
            <span className="tabular-nums">{time}</span>
          </div>
          <p
            className={cn(
              'mt-0.5 inline-block max-w-full rounded-lg rounded-tl-sm border border-dashed px-3 py-1.5 text-[13px] leading-relaxed whitespace-pre-wrap break-words',
              darkMode ? 'border-slate-700 bg-slate-900 text-slate-200' : 'border-slate-300 bg-white text-slate-700',
            )}
          >
            <TexteAvecMentions
              texte={event.details.content || event.summary}
              noms={mentions.map((m) => m.name)}
              darkMode={darkMode}
            />
          </p>
        </div>
      </div>
    )
  }

  // Note → carte centrée pleine largeur
  if (isCentered) {
    return (
      <div className="flex justify-center">
        <div
          className={cn(
            'max-w-[80%] rounded-lg px-3 py-2 text-[12px] border',
            darkMode
              ? 'border-dashed bg-slate-900 text-slate-200 border-slate-700'
              : 'border-dashed bg-white text-slate-700 border-slate-300',
          )}
        >
          <div
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wider mb-1',
              darkMode ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            {t('contacts.timeline.types.note', 'Note')}
            {event.agent_name && ` · ${event.agent_name}`} · {time}
          </div>
          <p className="leading-relaxed">
            {event.details.content || event.summary}
          </p>
        </div>
      </div>
    )
  }

  // SMS → bulle classique
  if (event.type === 'sms_inbound' || event.type === 'sms_outbound') {
    const media = event.details.media ?? []
    // Un MMS sans texte : on montre seulement ses médias.
    const body = event.details.body || (media.length === 0 ? event.summary : '')
    const isWhatsApp = event.details.channel === 'whatsapp'
    const smsId = Number(event.id.slice('sms_'.length)) || null
    return (
      <div className="group">
        <div
          className={cn(
            'flex',
            isOutbound ? 'justify-end' : 'justify-start',
          )}
        >
          <div className="max-w-[75%]">
            <SMSMediaList media={media} isOutbound={isOutbound} darkMode={darkMode} />
            {body && (
              <div
                className={cn(
                  'rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed',
                  isOutbound
                    ? 'bg-indigo-600 text-white rounded-br-md'
                    : darkMode
                      ? 'bg-slate-800/80 text-slate-200 rounded-bl-md'
                      : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100',
                )}
              >
                {body}
              </div>
            )}
            <div
              className={cn(
                'text-[10px] mt-0.5 px-1 opacity-0 group-hover:opacity-100 transition-opacity',
                darkMode ? 'text-slate-600' : 'text-gray-400',
                isOutbound ? 'text-right' : 'text-left',
              )}
            >
              {isWhatsApp && (
                <MessageCircle className="inline w-3 h-3 mr-1 -mt-0.5 text-green-500" />
              )}
              {time}
              {event.agent_name && ` · ${event.agent_name}`}
            </div>
          </div>
        </div>
        {phoneNumber && smsId !== null && (
          <DiscussionEquipe
            phoneNumber={phoneNumber}
            line={line}
            sujet={{ sms: smsId }}
            resume={event.details.discussion}
            equipe={equipe}
            aDroite={isOutbound}
            darkMode={darkMode}
          />
        )}
      </div>
    )
  }

  // Calls / voicemails → carte d'événement, alignée selon direction
  const isMissed =
    event.type === 'call_inbound' &&
    ['missed', 'no-answer', 'busy', 'failed', 'canceled'].includes(
      event.details.status || '',
    )
  const isVoicemail = event.type === 'voicemail'
  const duration = formatDuration(event.details.duration)

  const config = isVoicemail
    ? {
        icon: <VoicemailIcon className="w-4 h-4 text-white" />,
        bg: 'bg-orange-500',
        title: t('contacts.timeline.types.voicemail', 'Message vocal'),
      }
    : isMissed
      ? {
          icon: <PhoneMissed className="w-4 h-4 text-white" />,
          bg: 'bg-red-500',
          title: t('contacts.timeline.types.missedCall', 'Appel manqué'),
        }
      : isOutbound
        ? {
            icon: <PhoneOutgoing className="w-4 h-4 text-white" />,
            bg: 'bg-emerald-500',
            title: t('contacts.timeline.types.outboundCall', 'Appel sortant'),
          }
        : {
            icon: <PhoneIncoming className="w-4 h-4 text-white" />,
            bg: 'bg-indigo-600',
            title: t('contacts.timeline.types.inboundCall', 'Appel entrant'),
          }

  const callId = event.id.startsWith('call_') ? Number(event.id.slice('call_'.length)) : null
  const aResume = !!event.details.ai_summary
  const aActions =
    callId !== null &&
    event.details.status === 'completed' &&
    (aResume || (event.details.duration ?? 0) > 0)
  const aLecteur = !!(event.recording?.has_audio && phoneNumber && callId !== null)
  const transcription = isVoicemail ? event.details.transcription : ''
  const aCorps = aResume || aActions || aLecteur || !!transcription
  // Entrant : « répondu par X » ; sortant : juste le nom de l'agent qui a appelé.
  const qui = event.agent_name
    ? event.type === 'call_inbound' && !isMissed
      ? t('contacts.timeline.answeredBy', 'répondu par {{agent}}', { agent: event.agent_name })
      : event.agent_name
    : ''

  // Un appel = une carte : en-tête, résumé, actions et lecteur ensemble.
  // Dessous, la discussion de l'équipe sur cet appel.
  return (
    <div className="group">
      <div className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'rounded-xl border',
            aCorps ? 'w-full max-w-[460px]' : 'max-w-[80%]',
            darkMode ? 'bg-slate-800/60 border-slate-700/60' : 'bg-white border-gray-200 shadow-sm',
          )}
        >
          <div className="flex items-center gap-2.5 px-3 py-2.5">
            <div
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                config.bg,
              )}
            >
              {config.icon}
            </div>
            <div
              className={cn(
                'min-w-0 flex-1 text-[12.5px] font-semibold leading-tight',
                darkMode ? 'text-white' : 'text-gray-900',
              )}
            >
              {config.title}
              {duration && (
                <span className={cn('ml-1.5 font-normal', darkMode ? 'text-slate-400' : 'text-gray-500')}>
                  · {duration}
                </span>
              )}
            </div>
            <div
              className={cn(
                'ml-3 flex-shrink-0 text-[11px] tabular-nums whitespace-nowrap',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            >
              {time}
              {qui && ` · ${qui}`}
            </div>
          </div>

          {aCorps && (
            <div className="flex flex-col gap-2 px-3 pb-3">
              {transcription && (
                <p className={cn('text-[12px] leading-relaxed', darkMode ? 'text-slate-300' : 'text-gray-600')}>
                  {transcription}
                </p>
              )}

              {aResume && (
                <div
                  className={cn(
                    'rounded-lg px-3 py-2 text-[12px] leading-relaxed',
                    darkMode ? 'bg-violet-500/10 text-violet-100/90' : 'bg-indigo-50/80 text-indigo-950/80',
                  )}
                >
                  <span className="font-semibold mr-1">
                    ✨ {t('callDetail.aiSummary', 'Résumé IA')} :
                  </span>
                  {event.details.ai_summary}
                </div>
              )}

              {aActions && callId !== null && (
                <div className="flex items-start justify-between gap-2">
                  <ActionsResumeAppel
                    callId={callId}
                    resume={event.details.ai_summary || null}
                    enCours={!!event.details.resume_en_cours}
                    darkMode={darkMode}
                  />
                  {ouvrirDetails && (
                    <button
                      type="button"
                      onClick={() => ouvrirDetails(event)}
                      className={cn(
                        'flex-shrink-0 rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
                        darkMode
                          ? 'text-indigo-300 hover:bg-white/5'
                          : 'text-indigo-600 hover:bg-indigo-50',
                      )}
                    >
                      {t('callDetail.details', 'Détails')}
                    </button>
                  )}
                </div>
              )}

              {aLecteur && phoneNumber && callId !== null && (
                <LecteurAppel
                  phoneNumber={phoneNumber}
                  callId={callId}
                  darkMode={darkMode}
                  dureeConnue={event.recording?.duration}
                  aiCallId={event.recording?.ai_call_id}
                  integre
                />
              )}
            </div>
          )}

          {/* Mobile : pas de panneau Détails, le choix du dossier Clio est sur la carte */}
          {phoneNumber && callId !== null && (
            <DossierClio callId={callId} phoneNumber={phoneNumber} darkMode={darkMode} variante="carte" />
          )}
        </div>
      </div>
      {phoneNumber && callId !== null && !isMissed && (
        <DiscussionEquipe
          phoneNumber={phoneNumber}
          line={line}
          sujet={{ call: callId }}
          resume={event.details.discussion}
          equipe={equipe}
          aDroite={isOutbound}
          darkMode={darkMode}
        />
      )}
    </div>
  )
}
